"use client";

import { useState } from "react";
import { useSession } from "next-auth/react";
import dynamic from "next/dynamic";
import { useLocale, useTranslations } from "next-intl";
import Button from "@/components/ui/Button/Button";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { eventName, totalPieces } from "@/lib/avatar/events";
import { daysUntil, eventDay, phaseLabel } from "./eventTime";
import useAvatarEvent from "./useAvatarEvent";

// La fenêtre de l'événement n'est téléchargée que si le membre l'ouvre.
const EventModal = dynamic(() => import("./EventModal"), { ssr: false });

// Petit bloc du tableau de bord pendant un événement de puzzles : où en est le membre, et
// l'entrée vers ses puzzles. Rien ne s'affiche hors événement, ni une fois qu'il est terminé.
export default function EventDashboardCard({ className = "" }) {
    const t = useTranslations();
    const locale = useLocale();
    const { status } = useSession();
    const state = useAvatarEvent(status === "authenticated");
    const [open, setOpen] = useState(false);

    const event = state?.event;
    if (!event || event.phase === "closed") return null;
    const [label, values] = phaseLabel(event, locale);
    const total = totalPieces(event);
    const count = state.mine?.total || 0;
    const completed = state.mine?.completed || [];
    // Tout est fini : on ne promet plus de pièce, et le badge gagné prend la place de l'illustration.
    const allDone = total > 0 && count >= total;
    const picture = (allDone && event.theme?.badge) || (event.puzzles.find((puzzle) => puzzle.image && !completed.includes(puzzle.item)) || event.puzzles.find((puzzle) => puzzle.image))?.image;

    // Avant le début : une affiche aux couleurs de l'événement, qui montre ce qu'il y aura à gagner.
    // La salle de ligue l'affiche aussi (EventLeagueBand).
    if (event.phase === "upcoming") {
        const theme = event.theme || {};
        const color = theme.color || "#613BDB";
        const dark = theme.dark || "#2A1F4D";
        const pictures = event.puzzles.filter((puzzle) => puzzle.image);
        return (
            <>
                {/* La mise en ligne suit la largeur de la colonne, pas celle de l'écran : dans la colonne de droite du tableau de bord, l'affiche reste empilée. */}
                <div className={`@container ${className}`}>
                    <div className="relative flex flex-col gap-4 overflow-hidden rounded-2xl p-5 text-white @lg:flex-row @lg:items-center" style={{ background: `linear-gradient(120deg, ${dark}, ${dark} 50%, ${color})` }}>
                        {theme.badge && (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img src={theme.badge} alt="" className="h-24 w-24 shrink-0 self-center object-contain drop-shadow-[0_10px_18px_rgba(0,0,0,0.45)]" />
                        )}
                        <div className="min-w-0 flex-1">
                            <span className="mb-2 inline-block rounded-full bg-white/15 px-2.5 py-1 text-xs font-semibold">{t("avatar_event_teaser_days", { days: daysUntil(event.from) })}</span>
                            <p className="mb-1 text-lg font-extrabold leading-tight tracking-tight">{eventName(event, locale)}</p>
                            <p className="mb-3 text-sm text-white/80">{t("avatar_event_rule_upcoming", { date: eventDay(event.from, locale) })}</p>
                            <div className="flex flex-wrap items-center gap-3">
                                {pictures.length > 0 && (
                                    <span className="flex -space-x-2" aria-hidden="true">
                                        {pictures.slice(0, 5).map((puzzle) => (
                                            // eslint-disable-next-line @next/next/no-img-element
                                            <img key={puzzle.item} src={puzzle.image} alt="" className="h-9 w-9 rounded-lg object-cover ring-2" style={{ "--tw-ring-color": dark }} />
                                        ))}
                                    </span>
                                )}
                                <span className="text-xs font-medium text-white/85">{t(event.badgeId ? "avatar_event_teaser_rewards_badge" : "avatar_event_teaser_rewards", { count: event.puzzles.length })}</span>
                            </div>
                        </div>
                        <button type="button" onClick={() => setOpen(true)} className="shrink-0 self-start rounded-xl bg-white px-4 py-2.5 text-sm font-bold transition-transform duration-200 hover:scale-[1.03] active:scale-[0.98] @lg:self-center" style={{ color: dark }}>
                            {t("avatar_event_teaser_cta")}
                        </button>
                    </div>
                </div>
                {open && <EventModal isOpen onClose={() => setOpen(false)} />}
            </>
        );
    }

    return (
        <>
            <div className={`flex items-center gap-4 rounded-2xl border border-gray-200 bg-white p-4 dark:border-borderDark dark:bg-bgDark-800 ${className}`}>
                {/* L'illustration du premier puzzle encore à finir : on voit tout de suite ce qu'on est en train de gagner. */}
                {picture ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={picture} alt="" className={`h-14 w-14 shrink-0 ${allDone && event.theme?.badge ? "object-contain" : "rounded-2xl object-cover"}`} />
                ) : (
                    <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary/10">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src="/static/icons/puzzle-piece.svg" alt="" className="h-8 w-8" />
                    </span>
                )}
                <div className="min-w-0 flex-1">
                    <p className="mb-0.5 flex flex-wrap items-center gap-x-2 text-sm font-semibold text-gray-900 dark:text-white">
                        {eventName(event, locale)}
                        <span className="text-xs font-normal text-gray-500 dark:text-gray-400">{t(label, values)}</span>
                    </p>
                    <p className="mb-2 text-sm text-gray-500 dark:text-textSecondDark">{t(allDone ? "avatar_event_card_done" : "avatar_event_card_text")}</p>
                    {event.phase !== "upcoming" && (
                        <div className="flex items-center gap-3">
                            <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-white/10">
                                <div className="h-full rounded-full bg-primary transition-[width] duration-500 ease-out" style={{ width: `${total ? Math.round((count / total) * 100) : 0}%` }} />
                            </div>
                            <span className="shrink-0 text-xs tabular-nums text-gray-500 dark:text-gray-400">{t("avatar_event_card_progress", { count, total })}</span>
                        </div>
                    )}
                </div>
                <Button type="secondary" small onClick={() => setOpen(true)}>
                    {t("avatar_event_card_cta")}
                </Button>
            </div>
            {open && <EventModal isOpen onClose={() => setOpen(false)} />}
        </>
    );
}
